import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Card, Flex, Text } from "@jsq/ds/core";
import { ComplianceObjectType } from "../../../../graphql/supergraphEnums";
import { isIndividualAccountType } from "../../../../utils/account_types";
import { TreeComponent } from "../../../common/TreeComponent";
import {
  AUDIT_LOG_SUMMARY_PREVIEW_LIMIT,
  ComplianceActivityCard,
} from "../../ComplianceActivityCard";
import { AmlKycStatusCard } from "./AmlKycStatusCard";
import {
  buildPositionHierarchy,
  buildTreeNode,
  toHierarchyNodeData,
} from "./PositionNodeCard/utils";
import { auditColumn, cardSubTitle, summaryContentRow, treeColumn } from "./styles_vanilla.css";
import { HierarchyChildProfile, HierarchyRootProfile, PositionNode } from "./types";

type Props = {
  positionName: string;
  rootProfile?: HierarchyRootProfile | null;
  childProfiles: HierarchyChildProfile[];
  positionNode: PositionNode;
  arenaId: number;
  arenaName?: string;
  positionId: number;
  onViewAllAudit: () => void;
};

export const PositionSummary = ({
  positionName,
  rootProfile,
  childProfiles,
  positionNode,
  arenaId,
  arenaName,
  positionId,
  onViewAllAudit,
}: Props) => {
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeBounds, setTreeBounds] = useState<DOMRect>();

  const isIndividual =
    rootProfile?.complianceObjectType == ComplianceObjectType.Individual ||
    isIndividualAccountType(rootProfile?.accountType);

  const treeData = useMemo(() => {
    if (!rootProfile) return null;
    const hierarchy = buildPositionHierarchy(rootProfile, childProfiles);
    return buildTreeNode(toHierarchyNodeData(hierarchy, positionNode?.programId ?? undefined));
  }, [rootProfile, childProfiles, positionNode?.programId]);

  useLayoutEffect(() => {
    const measureBounds = () => {
      if (treeContainerRef.current) {
        const newBounds = treeContainerRef.current.getBoundingClientRect();
        setTreeBounds((prev) =>
          !prev ||
          Math.abs(prev.width - newBounds.width) > 1 ||
          Math.abs(prev.height - newBounds.height) > 1
            ? newBounds
            : prev,
        );
      }
    };

    measureBounds();

    const timeoutId = setTimeout(measureBounds, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <Card
        title="AML/KYC status"
        subTitle={
          <Text variant="regular-sm" className={cardSubTitle}>
            {positionName}
          </Text>
        }
      >
        <AmlKycStatusCard positionNode={positionNode} rootProfile={rootProfile} />
      </Card>
      <Flex alignItems="flex-start" className={summaryContentRow}>
        <div className={treeColumn}>
          <Card title={`${positionName} ${isIndividual ? "" : "organization structure"}`}>
            <div ref={treeContainerRef} role="tree">
              {treeData && (
                <TreeComponent
                  data={treeData}
                  rootEdgeStyle={{ dashWidth: 3, dashGap: 3 }}
                  treeConfig={{ nodeMaxWidth: treeBounds?.width }}
                />
              )}
            </div>
          </Card>
        </div>
        <div className={auditColumn}>
          <ComplianceActivityCard
            arenaId={arenaId}
            arenaName={arenaName}
            positionId={positionId}
            maxVisibleItems={AUDIT_LOG_SUMMARY_PREVIEW_LIMIT}
            onViewAll={onViewAllAudit}
          />
        </div>
      </Flex>
    </>
  );
};
